import type { BaseDevice } from '../../../runtime/BaseDevice'
import { DeviceError, DeviceErrorCode } from '../../../errors/DeviceError'
import { ConfigFeature } from '../../../features/config/contract'
import type { Config } from '../../../types/config'
import { CH315_DEVICE_SELECT, type CH315KeyboardProtocol } from '../protocols'
import { CH315_FEATURE_LAYOUT, getCH315ConfigBase } from '../types/feature-types'
import { CH315FeatureMemory, decodeCH315Text } from './memory'

export class CH315Config extends ConfigFeature {
  private readonly memory: CH315FeatureMemory

  constructor(
    device: BaseDevice,
    private readonly protocol: CH315KeyboardProtocol,
  ) {
    super(device)
    this.memory = new CH315FeatureMemory(protocol)
  }

  async getCurrentConfig(): Promise<Config> {
    const index = await this.getCurrentConfigIndex()
    const name = await this.getCurrentConfigName(index).catch(() => `Config ${index + 1}`)
    const config = { index, name }
    this.device.currentConfig = config
    return config
  }

  async getCurrentConfigIndex(): Promise<number> {
    const index = await this.protocol.getCurrentConfig()
    assertConfigIndex(index)
    return index
  }

  async getCurrentConfigName(index?: number): Promise<string> {
    const target = index ?? (await this.getCurrentConfigIndex())
    assertConfigIndex(target)
    const data = await this.memory.read(
      getCH315ConfigBase(target) + CH315_FEATURE_LAYOUT.configNameOffset,
      CH315_FEATURE_LAYOUT.macroNameBytes,
    )
    return decodeCH315Text(data)
  }

  async getAllConfigs(): Promise<Config[]> {
    const configs = await Promise.all(
      Array.from({ length: CH315_FEATURE_LAYOUT.configCount }, async (_, index) => ({
        index,
        name: await this.getCurrentConfigName(index),
      })),
    )
    this.device.configList = configs
    return configs
  }

  async setCurrentConfig(index: number): Promise<void> {
    assertConfigIndex(index)
    await this.protocol.selectConfig(index)
    const known = this.device.configList.find((config) => config.index === index)
    this.device.currentConfig = known ?? {
      index,
      name: await this.getCurrentConfigName(index),
    }
  }

  async createConfig(name: string, index?: number): Promise<number> {
    const normalized = validateConfigName(name)
    let target = index
    if (target === undefined) {
      const configs = await this.getAllConfigs()
      target = configs.find((config) => config.name === '')?.index
      if (target === undefined) {
        throw new DeviceError(DeviceErrorCode.MAX_LIMIT_REACHED, 'CH315 has no empty config slot')
      }
    }
    assertConfigIndex(target)
    await this.protocol.createConfig(target, normalized)
    this.device.configList = this.device.configList.filter((item) => item.index !== target)
    this.device.configList.push({ index: target, name: normalized })
    return target
  }

  async deleteConfig(index: number): Promise<void> {
    assertConfigIndex(index)
    if (this.device.currentConfig.index === index) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        'Switch to another CH315 config before deleting the current config',
      )
    }
    await this.protocol.deleteConfig(index)
    this.device.configList = this.device.configList.map((config) =>
      config.index === index ? { index, name: '' } : config,
    )
  }

  async initAllConfigs(): Promise<void> {
    await this.protocol.restoreFactoryDefaults(CH315_DEVICE_SELECT.all)
    this.device.configList = []
    this.device.currentConfig = { index: 0, name: '出厂配置' }
  }
}

function assertConfigIndex(index: number): void {
  if (!Number.isInteger(index) || index < 0 || index >= CH315_FEATURE_LAYOUT.configCount) {
    throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'CH315 config index must be 0..3')
  }
}

function validateConfigName(name: string): string {
  const normalized = name.trim()
  if (
    normalized.length === 0 ||
    normalized.length > CH315_FEATURE_LAYOUT.macroNameBytes ||
    !/^[\x20-\x7e]+$/.test(normalized)
  ) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      'CH315 config name must contain 1..16 printable ASCII characters',
    )
  }
  return normalized
}
